import { ModelEntityType } from '@upright/common/model/input/types';
import { Dimension, Valence } from '@upright/common/model/types';
import { openChatPanel } from '@upright/ui/chat/chat-panel-state';
import { useIsTeam } from '@upright/ui/common/generic/hooks';
import { valenceColor } from '@upright/ui/common/generic/utilities/format';
import {
  buildDrivingImpactPrompt,
  dimensionAdjective,
} from '@upright/ui/common/upright/core/impact-lens-profile/frame/chat-prompt';
import { buildNetImpactDrillInPayload } from '@upright/ui/common/upright/core/impact-lenses/net-impact/drill-in-panel/build-payload';
import { openNetImpactDrillIn } from '@upright/ui/common/upright/core/impact-lenses/net-impact/drill-in-panel/state';
import { LensImpactProfileBar } from '@upright/ui/common/upright/core/impact-lens-profile/common/components/bars/bar';
import { LensGhostTail } from '@upright/ui/common/upright/core/impact-lens-profile/common/components/bars/ghost-tail';
import { PlaceholderSVG } from '@upright/ui/common/upright/core/impact-lens-profile/common/components/placeholder';
import { dimensionBarsLayout } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/dimension-bars-layout';
import { valenceToSide } from '@upright/ui/common/upright/core/impact-lens-profile/common/utils';
import { useCarouselContext, useCarouselLayout } from '@upright/ui/common/upright/core/impact-lens-profile/context';
import { useNetImpactLensContext } from '@upright/ui/common/upright/core/impact-lenses/net-impact/common/use-net-impact-lens-context';
import { NetImpactLensSelectionState } from '@upright/ui/common/upright/core/impact-lenses/net-impact/selection-state';
import { useReleaseFeatureGate } from '@upright/ui/common/upright/model/output/releases/hooks';
import { getImpactBarId } from '@upright/test-ids/dom-elements';
import styled, { css } from 'styled-components';

interface Props {
  dimension: Dimension;
  valence: Valence;
}

export function DimensionBar(props: Props) {
  const baseLayout = useCarouselLayout();
  const ctx = useNetImpactLensContext();
  const carousel = useCarouselContext();
  const isTeam = useIsTeam();
  // Mirror the impact-bar's value-chain gate so the drill-in panel's
  // contributors table splits by value chain exactly when the chart does.
  // Hooks must run before the early return below.
  const allowValueChains = useReleaseFeatureGate('value-chain-splits');
  const showValueChains =
    Boolean((carousel.selectionState as NetImpactLensSelectionState).showValueChains) && allowValueChains;
  // Narrow `ctx` (not just `ctx.data.scores`) so the click handler below
  // can read `ctx.options` / `ctx.data` without a non-null assertion.
  if (!ctx || !ctx.data?.scores) {
    return <></>;
  }
  const data = ctx.data.scores;
  // Hoist into narrowed consts so the click handler closure reads them
  // without re-narrowing `ctx` (control-flow narrowing of `ctx` doesn't
  // carry into the nested `handleClick`).
  const netOptions = ctx.options;
  const netData = ctx.data;

  // `scoreUnit` is not passed here (matches the pre-ghost behavior of this
  // call site), but `ghostScores` must be — it feeds the shared `maxX`, and
  // every layout instance for the same profile must agree on the x-scale.
  const layout = dimensionBarsLayout(baseLayout, data, undefined, netData.ghostScores);
  const barArea = layout.dimensionBarArea(valenceToSide(props.valence), props.dimension);
  // Ghost-baseline tail (see `NetImpactLensData.ghostScores`) — undefined for
  // every profile that doesn't set ghost data, i.e. all production profiles.
  const ghostTailArea = layout.dimensionGhostTailArea(valenceToSide(props.valence), props.dimension);
  // Hit-area covers the full bar range slot (not just the visible portion of
  // the bar, which scales with score) so the click target stays consistent
  // even for short bars. Mirrors how `impact-bars.tsx` uses
  // `impactDataArea` for its `Clickable`.
  const hitArea = layout.dimensionBarRangeArea(valenceToSide(props.valence), props.dimension);

  function getFill() {
    return valenceColor(props.valence);
  }

  // PLT-4494: team-only deep link from a dimension bar into the AI chat with
  // a pre-filled "what is driving …" question scoped to the entity rendered
  // on this profile (company, group, or portfolio).
  //
  // PLT-4625: this used to `navigate('/chat?prompt=…&entityId=…')`, taking
  // the user fully out of the page they were exploring. Now it opens the
  // right-side `ChatSidePanel` instead — same prompt, same entity binding,
  // same chat features — and the user can decide whether to keep the page
  // visible underneath or click "Full" in the panel header to expand into
  // the full `/chat` route. The `openChatPanel` API does its own thread
  // reset on the shared app-root runtime, so each bar click starts a fresh
  // conversation.
  const entity = carousel.data.entity;
  const isCompany = entity?.type === ModelEntityType.COMPANY;
  // PLT-4940 / PLT-5427: company dimension bars open the dimension-grain
  // drill-in panel (same surface the impact bars open at impact grain),
  // now for every viewer — the per-user variation lives inside the
  // panel's Chat tab. Group / portfolio dimension bars have no
  // per-company drilldown, so they keep the legacy team-only chat
  // deep-link.
  const opensDrillIn = isCompany;
  const opensChat = isTeam && entity !== undefined && !isCompany;
  const clickable = opensDrillIn || opensChat;

  function handleClick() {
    if (!entity) return;
    if (opensDrillIn) {
      openNetImpactDrillIn({
        payload: buildNetImpactDrillInPayload({
          target: { grain: 'dimension', dimension: props.dimension },
          entity: { id: entity.id, type: entity.type, name: entity.name },
          scoreReader: netOptions.scoreReader,
          revenue: netData.revenue,
          modelResultId: netData.modelResultId,
          productNames: netData.productNames,
          hasProductViewAccess: netData.hasProductViewAccess,
          showValueChains,
        }),
        initialTab: props.valence === Valence.NEGATIVE ? 'negative' : 'positive',
      });
      return;
    }
    // Re-check `entity` defensively to narrow the type for TS — `clickable`
    // already implies `entity !== undefined`, but the boolean doesn't carry
    // the narrowing forward.
    if (!opensChat) return;
    const entityName = entity.name ?? 'this entity';
    // PLT-4700 (Slack thread, "Same for everything"): dimension-bar
    // clicks now send the same un-valenced "what is driving" prompt as
    // the dimension-label, impact-title, and impact-bar surfaces. The
    // valence the user expressed by clicking the colored bar is carried
    // by the in-card valence pill on the contributing-products card —
    // it no longer needs to ride in the prompt wording. See
    // `chat-prompt.ts` for the full rationale.
    const prompt = buildDrivingImpactPrompt(dimensionAdjective(props.dimension), entityName);
    // PLT-4625 follow-up: expose a visible page-context chip above the
    // chat thread (and feed the same URL into the model's hidden context
    // line) so the user can see — and the model knows — which page the
    // question originates from. Path-only is enough for the model and
    // also makes the chip's "open the page in a new tab" affordance
    // self-relative-routable.
    const pageHref =
      typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}${window.location.hash}`
        : '';
    openChatPanel({
      prompt,
      entityId: entity.id,
      entityType: entity.type,
      source: 'dimension_bar',
      pageContext: {
        label: `${entityName} — Net impact`,
        href: pageHref,
        lensLabel: 'Net impact',
      },
    });
  }

  const testId = getImpactBarId(props.dimension, props.valence);

  return (
    <G data-testid={testId} clickable={clickable} onClick={handleClick}>
      <Bar bounds={barArea} background={getFill()} border="transparent" />
      {ghostTailArea && (
        <LensGhostTail
          bounds={ghostTailArea}
          fill={getFill()}
          patternId={`lens-dimension-ghost-${entity?.id ?? ''}-${props.dimension}-${props.valence}`}
        />
      )}
      {clickable && <Clickable bounds={hitArea} />}
    </G>
  );
}

// PLT-4700: `valenceWord` and `dimensionWord` were inlined here pre-
// unification. They moved to `frame/chat-prompt.ts` (dimension adjective
// only — valence is no longer part of the prompt wording; see the
// `handleClick` comment above).

const Bar = styled(LensImpactProfileBar)``;

// Transparent SVG rectangle that captures pointer events across the full bar
// range slot. `pointer-events: fill` is required because SVG `<rect>` with
// `fill="transparent"` is not clickable by default (default `pointer-events:
// visiblePainted` excludes invisible fills). Same trick as in `impact-bars.tsx`.
const Clickable = styled(PlaceholderSVG)`
  opacity: 0;
  pointer-events: fill;
`;

const G = styled.g<{ clickable: boolean }>`
  ${p =>
    p.clickable
      ? css`
          cursor: pointer;
          &:hover ${Bar} {
            opacity: 0.7;
          }
        `
      : ''};
`;
